/**
 * SfuCredentialsPage — Super-admin only.
 *
 * Manages the LiveKit (or "none") SFU configuration for the screen-share
 * subsystem. Mirrors PaymentCredentialsPage UX so admins find the page
 * familiar.
 *
 * The API secret is NEVER returned by the GET endpoint — the masked form
 * (length + last 4 chars) is shown instead. Leaving the secret field
 * blank on save keeps the previously-saved value (so an admin editing
 * the URL doesn't have to re-paste the secret).
 *
 * Test connection mints a throwaway 60-second JWT against the currently-
 * persisted credentials. A green checkmark + token URL means LiveKit is
 * actually reachable; red means the SDK rejected the inputs.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Monitor, FloppyDisk, Eye, EyeSlash,
  CheckCircle, Warning, Spinner, ArrowsClockwise,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import AccountMenu from "@/components/AccountMenu";

export default function SfuCredentialsPage({ user, onLogout, onLogoutAll }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [edits, setEdits] = useState({}); // {provider, livekit_url, livekit_api_key, livekit_api_secret, livekit_max_participants}
  const [revealSecret, setRevealSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(""); setTestResult(null);
    try {
      const r = await api.get("/conv/admin/sfu-config");
      setData(r.data);
      setEdits({});
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not load SFU configuration");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setEdits((p) => ({ ...p, [k]: v }));
  const dirty = Object.keys(edits).length > 0;
  const provider = edits.provider ?? data?.provider ?? "none";

  const onSave = async () => {
    setSaving(true); setError(""); setTestResult(null);
    try {
      const payload = {
        provider,
        livekit_url: edits.livekit_url ?? data.livekit_url ?? "",
        livekit_api_key: edits.livekit_api_key ?? data.livekit_api_key ?? "",
        // Always leave secret blank on the wire when the admin
        // didn't explicitly type a new one — backend keeps the
        // previous value in that case.
        livekit_api_secret: edits.livekit_api_secret ?? "",
        livekit_max_participants:
          edits.livekit_max_participants ?? data.livekit_max_participants ?? 50,
      };
      const r = await api.put("/conv/admin/sfu-config", payload);
      setData(r.data);
      setEdits({});
      setRevealSecret(false);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not save SFU configuration");
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post("/conv/admin/sfu-config/test");
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || e?.message });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sfu-credentials-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate(-1)} className="text-slate-500 hover:text-slate-800 shrink-0" data-testid="sfu-back">
              <ArrowLeft size={18} weight="bold" />
            </button>
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Admin</div>
              <h1 className="text-lg font-black tracking-tighter font-['Chivo'] flex items-center gap-2">
                <Monitor size={18} weight="duotone" style={{ color: "#00a884" }} />
                Screen Share / SFU Credentials
              </h1>
            </div>
          </div>
          <AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 md:p-8">
        <div className="mb-4">
          <p className="text-sm text-slate-600">
            Choose how screen-share frames are routed between participants. Setting
            this page takes effect <strong>instantly</strong> — no restart needed.
            For LiveKit, sign up at <a href="https://livekit.io" target="_blank" rel="noreferrer" className="underline">livekit.io</a> and grab a project's API Key + Secret from the Cloud dashboard.
          </p>
          <div className="mt-3 text-[11px] font-bold uppercase tracking-wider text-amber-800 bg-amber-50 border border-amber-200 px-2 py-1 rounded-sm inline-flex items-center gap-1">
            <ShieldCheck size={11} weight="bold" /> Super-admin required
          </div>
        </div>

        {error && (
          <div className="mb-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-sm p-3" data-testid="sfu-error">
            {error}
          </div>
        )}

        {loading && !data ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Spinner size={16} className="animate-spin" /> Loading…
          </div>
        ) : data && (
          <section className="border border-slate-200 rounded-md bg-white overflow-hidden">
            {/* Provider selector */}
            <div className="p-4 border-b border-slate-100">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">SFU Provider</div>
              <div className="inline-flex items-center rounded-sm border border-slate-200 overflow-hidden">
                {[
                  { id: "none", label: "None (P2P only)", color: "bg-slate-600" },
                  { id: "livekit", label: "LiveKit", color: "bg-emerald-600" },
                ].map((p) => {
                  const active = provider === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => setField("provider", p.id)}
                      data-testid={`sfu-provider-${p.id}`}
                      className={`h-8 px-4 text-[11px] font-bold uppercase tracking-wider transition-colors ${
                        active ? `${p.color} text-white` : "text-slate-500 hover:bg-slate-100"
                      }`}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
              <div className="mt-2 text-[11px] text-slate-500">
                {provider === "none"
                  ? "Direct peer-to-peer with no media server. Capped at small viewer counts; falls back to a placeholder above the cap."
                  : "Frames routed through a hosted SFU. Best for >3 viewers and unstable networks. Requires API credentials below."}
              </div>
            </div>

            {/* LiveKit fields — only visible when LiveKit is the provider */}
            {provider === "livekit" && (
              <div className="p-4 space-y-3 border-b border-slate-100">
                <div>
                  <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">URL</Label>
                  <Input
                    type="text"
                    placeholder="wss://your-project.livekit.cloud"
                    value={edits.livekit_url ?? data.livekit_url ?? ""}
                    onChange={(e) => setField("livekit_url", e.target.value)}
                    data-testid="sfu-input-url"
                    className="font-mono text-sm"
                  />
                  <div className="mt-1 text-[11px] text-slate-400">Found in your LiveKit Cloud project settings under "Project URL".</div>
                </div>
                <div>
                  <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">API Key</Label>
                  <Input
                    type="text"
                    placeholder="APIxxxxxxxxxxxx"
                    value={edits.livekit_api_key ?? data.livekit_api_key ?? ""}
                    onChange={(e) => setField("livekit_api_key", e.target.value)}
                    data-testid="sfu-input-key"
                    className="font-mono text-sm"
                  />
                </div>
                <div>
                  <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">API Secret</Label>
                  <div className="relative">
                    <Input
                      type={revealSecret ? "text" : "password"}
                      placeholder={
                        data.livekit_api_secret_masked?.has_value
                          ? `•••••••••••••${data.livekit_api_secret_masked.last4} (saved — leave blank to keep)`
                          : "Paste your LiveKit API secret"
                      }
                      value={edits.livekit_api_secret ?? ""}
                      onChange={(e) => setField("livekit_api_secret", e.target.value)}
                      data-testid="sfu-input-secret"
                      className="font-mono text-sm pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setRevealSecret((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      title={revealSecret ? "Hide" : "Reveal"}
                      data-testid="sfu-secret-toggle"
                    >
                      {revealSecret ? <EyeSlash size={14} weight="bold" /> : <Eye size={14} weight="bold" />}
                    </button>
                  </div>
                  <div className="mt-1 text-[11px] text-slate-400">
                    Secret is stored encrypted-at-rest in MySQL and never echoed back. Leave blank to keep the existing value.
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Max participants per share</Label>
                  <Input
                    type="number"
                    min="1"
                    max="500"
                    value={edits.livekit_max_participants ?? data.livekit_max_participants ?? 50}
                    onChange={(e) => setField("livekit_max_participants", parseInt(e.target.value, 10) || 50)}
                    data-testid="sfu-input-max"
                    className="font-mono text-sm w-32"
                  />
                </div>
              </div>
            )}

            {/* Status footer */}
            <div className="p-4 bg-slate-50 flex flex-wrap items-center justify-between gap-3">
              <div className="text-[11px] text-slate-500">
                <div>
                  Provider currently active in this process: <span className="font-bold text-slate-700" data-testid="sfu-live-name">{data.live?.name || "stub"}</span>
                  {data.live?.configured ? (
                    <span className="ml-2 inline-flex items-center gap-1 text-emerald-700">
                      <CheckCircle size={11} weight="bold" /> configured
                    </span>
                  ) : (
                    <span className="ml-2 inline-flex items-center gap-1 text-slate-400">
                      <Warning size={11} weight="bold" /> not configured
                    </span>
                  )}
                </div>
                {data.updated_at && (
                  <div className="mt-1">
                    Last saved by <span className="font-mono">{data.updated_by}</span> at {new Date(data.updated_at).toLocaleString("en-US")}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={onTest}
                  disabled={testing || dirty || !data.live?.configured}
                  data-testid="sfu-test-btn"
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-sm border border-slate-300 hover:bg-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-600 disabled:opacity-50"
                  title={dirty ? "Save first" : "Mint a throwaway 60-second token to verify credentials"}
                >
                  {testing ? <Spinner size={11} className="animate-spin" /> : <ArrowsClockwise size={11} weight="bold" />}
                  Test connection
                </button>
                <button
                  onClick={onSave}
                  disabled={!dirty || saving}
                  data-testid="sfu-save-btn"
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-sm text-white text-[11px] font-bold uppercase tracking-wider disabled:opacity-50"
                  style={{ background: "#00a884" }}
                >
                  {saving ? <Spinner size={11} className="animate-spin" /> : <FloppyDisk size={11} weight="bold" />}
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>

            {testResult && (
              <div
                className={`px-4 py-2.5 text-[11px] flex items-center gap-2 border-t ${
                  testResult.ok
                    ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                    : "bg-rose-50 text-rose-700 border-rose-200"
                }`}
                data-testid="sfu-test-result"
              >
                {testResult.ok ? <CheckCircle size={13} weight="bold" /> : <Warning size={13} weight="bold" />}
                {testResult.ok ? (
                  <span>
                    Connection OK · provider={testResult.provider} · url={testResult.url} · token expires in {testResult.expires_in_seconds}s
                  </span>
                ) : (
                  <span>Connection failed: {testResult.error}</span>
                )}
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
